import './BillToBlock.css';

interface BillToBlockProps {
  /** Lines from the API's `bill_to` — composed by billing/bill-to.ts. */
  lines: readonly string[] | undefined;
  /** Heading above the block. */
  label?: string;
  /** Shown instead of the block when there is nothing to address. */
  empty?: string;
}

/**
 * The invoice addressee, exactly as the PDF prints it.
 *
 * The lines are composed **server-side** so the client page, the project page,
 * the PDF preview and the invoice itself are one implementation — a preview
 * that disagrees with the document is worse than no preview. This component
 * only renders them.
 */
export function BillToBlock({
  lines,
  label = 'Bill To',
  empty = 'No addressee yet.',
}: BillToBlockProps): JSX.Element {
  const rows = lines ?? [];
  return (
    <div className="billto">
      <div className="billto-label">{label}</div>
      {rows.length === 0 ? (
        <p className="billto-empty">{empty}</p>
      ) : (
        <address className="billto-lines">
          {rows.map((line, i) => (
            // Index keys: these are positional lines of one address, not a
            // reorderable list. A blank line comes from a typed override and
            // has to keep its height.
            <span key={i} className={i === 0 ? 'billto-line billto-line-lead' : 'billto-line'}>
              {line === '' ? ' ' : line}
            </span>
          ))}
        </address>
      )}
    </div>
  );
}
